
import { useCallback } from "react";
import { Position, GeoLocationOptions } from "@/types/locationTypes";

/**
 * Hook for handling position tracking functionality
 */
export const usePositionTracking = () => {
  // Get current position
  const getCurrentPosition = useCallback((): Promise<Position> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("La géolocalisation n'est pas disponible sur votre appareil"));
        return;
      }

      const options: GeoLocationOptions = {
        enableHighAccuracy: true, // High accuracy for golf
        timeout: 10000, // Timeout after 10 seconds
        maximumAge: 0, // No cache
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: position.timestamp,
          });
        },
        (error) => {
          let errorMessage = "Erreur de géolocalisation";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Vous avez refusé l'accès à la géolocalisation";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Les informations de position ne sont pas disponibles";
              break;
            case error.TIMEOUT:
              errorMessage = "La demande de position a expiré";
              break;
          }
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }, []);

  // Setup position watching
  const watchPosition = useCallback((
    onSuccess: (position: Position) => void,
    onError: (error: GeolocationPositionError) => void
  ): (() => void) => {
    const options: GeoLocationOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };
    
    // Start tracking
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const currentPosition: Position = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: position.timestamp
        };
        onSuccess(currentPosition);
      },
      onError,
      options
    );
    
    // Return cleanup function
    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  return {
    getCurrentPosition,
    watchPosition
  };
};
